// src/pages/DebtStudio/lib/buildDebtServiceSchedule.js
import dayjs from "lib/dayjs";
import { dayCountFraction } from "lib/dayCount";

/**
 * Normalize coupons for term bond sinking fund rows:
 * Identify the parent (final maturity in each term_group with non-null params),
 * propagate the parent’s coupon, yield, and price to all rows in that group.
 */
export function normalizeTermCoupons(rows = []) {
  if (!rows?.length) return [];

  const groups = new Map();
  for (const r of rows) {
    if (r.bond_type === "term" && r.term_group) {
      if (!groups.has(r.term_group)) groups.set(r.term_group, []);
      groups.get(r.term_group).push(r);
    }
  }

  const out = [...rows];

  for (const [groupKey, groupRows] of groups.entries()) {
    const parent = groupRows.reduce((latest, r) => {
      const hasParams = r.coupon != null || r.yield != null || r.price != null;
      if (!hasParams) return latest;
      if (!latest) return r;
      return dayjs(r.date).isAfter(latest.date) ? r : latest;
    }, null);

    if (parent) {
      for (let i = 0; i < out.length; i++) {
        const r = out[i];
        if (r.term_group === groupKey) {
          out[i] = {
            ...r,
            coupon: parent.coupon ?? r.coupon,
            yield: parent.yield ?? r.yield,
            price: parent.price ?? r.price,
          };
        }
      }
    }
  }

  return out;
}

/* ------------------------------------------------------------------
 * Numeric helpers
 * ------------------------------------------------------------------ */

function num(x, dflt = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : dflt;
}

function round2(x) {
  const n = num(x, 0);
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function parseCouponAnnual(v) {
  if (v == null || v === "") return 0;
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;

  // DebtStudio maturity coupons are stored and edited as percentage points
  // (for example, 0.900 means 0.900%, not a 0.900 decimal rate).
  return n / 100;
}

function isLoanFinancing(form) {
  return (
    String(form?.financingType || form?.financing_type || "")
      .trim()
      .toLowerCase() === "loan"
  );
}

function getLoanPeriodRate(outstandingMaturities, periodEnd) {
  const rateRow =
    outstandingMaturities.find((m) =>
      dayjs(m.date).isSame(periodEnd, "day")
    ) || outstandingMaturities[0];

  const rate = Number(rateRow?.coupon);
  return Number.isFinite(rate) && rate >= 0 ? rate : 0;
}

/* ------------------------------------------------------------------
 * Schedule helpers
 * ------------------------------------------------------------------ */

function getPeriodsPerYear(couponFrequency) {
  const freq = String(couponFrequency || "semiannual").toLowerCase();

  if (freq.startsWith("month")) return 12;
  if (freq.startsWith("quarter")) return 4;
  if (freq.startsWith("annual")) return 1;

  return 2;
}

function buildInterestDates({
  datedDate,
  firstCouponDate,
  finalMaturityDate,
  stepMonths,
}) {
  const start = dayjs(datedDate);
  const final = dayjs(finalMaturityDate);

  if (!start.isValid() || !final.isValid()) return [];

  let d = firstCouponDate
    ? dayjs(firstCouponDate)
    : start.add(stepMonths, "month");
  if (!d.isValid()) return [];

  const dates = [];

  for (let guard = 0; guard < 2000 && !d.isAfter(final, "day"); guard++) {
    if (d.isAfter(start, "day")) {
      dates.push(d.format("YYYY-MM-DD"));
    }

    d = d.add(stepMonths, "month");
  }

  return dates;
}

function buildPrincipalDates({ maturities, datedDate, finalMaturityDate }) {
  const start = dayjs(datedDate);
  const final = dayjs(finalMaturityDate);

  if (!start.isValid() || !final.isValid()) return [];

  return maturities
    .map((m) => dayjs(m.date))
    .filter((d) => d.isValid())
    .filter((d) => d.isAfter(start, "day") && !d.isAfter(final, "day"))
    .map((d) => d.format("YYYY-MM-DD"));
}

function sortIsoDates(a, b) {
  return dayjs(a).valueOf() - dayjs(b).valueOf();
}

function computeSettlementAccruedInterest({
  maturities = [],
  datedDate,
  deliveryDate,
  dayCount,
}) {
  const start = datedDate ? dayjs(datedDate).startOf("day") : null;
  const delivery = deliveryDate ? dayjs(deliveryDate).startOf("day") : null;

  if (!start || !delivery || !start.isValid() || !delivery.isValid()) return 0;
  if (!delivery.isAfter(start, "day")) return 0;

  const frac = dayCountFraction(start, delivery, dayCount || "30/360");
  if (!Number.isFinite(frac) || frac <= 0) return 0;

  const accruedRaw = maturities.reduce((sum, m) => {
    const principal = num(m?.principal, 0);
    const couponAnnual = parseCouponAnnual(m?.coupon);
    return sum + principal * couponAnnual * frac;
  }, 0);

  return round2(accruedRaw);
}

function normalizeBaseDebtServiceFields(cashflows = []) {
  return cashflows.map((r) => {
    const grossDebtService = round2(num(r?.total, 0));

    return {
      ...r,
      debtServiceAdjustment: round2(
        num(r?.debtServiceAdjustment ?? r?.adjustment, 0)
      ),
      adjustment: round2(num(r?.debtServiceAdjustment ?? r?.adjustment, 0)),
      adjustmentLabel: r?.adjustmentLabel || "",
      adjustments: Array.isArray(r?.adjustments) ? r.adjustments : [],
      netDebtService: round2(
        Number.isFinite(Number(r?.netDebtService))
          ? Number(r.netDebtService)
          : grossDebtService + num(r?.debtServiceAdjustment ?? r?.adjustment, 0)
      ),
    };
  });
}

function applyFirstDebtServiceAdjustment(cashflows = [], adjustmentAmount = 0) {
  const adjustment = round2(adjustmentAmount);
  const base = normalizeBaseDebtServiceFields(cashflows);

  if (!base?.length || adjustment <= 0) return base;

  let applied = false;

  return base.map((r) => {
    const grossDebtService = round2(num(r?.total, 0));

    if (!applied && grossDebtService !== 0) {
      applied = true;

      const accruedAdjustment = round2(-adjustment);
      const existingAdjustment = round2(
        num(r?.debtServiceAdjustment ?? r?.adjustment, 0)
      );
      const debtServiceAdjustment = round2(
        existingAdjustment + accruedAdjustment
      );

      return {
        ...r,
        adjustments: [
          ...(Array.isArray(r?.adjustments) ? r.adjustments : []),
          {
            label: "Accrued Interest",
            amount: accruedAdjustment,
          },
        ],
        debtServiceAdjustment,
        adjustment: debtServiceAdjustment,
        adjustmentLabel: [r?.adjustmentLabel, "Accrued Interest"]
          .filter(Boolean)
          .join(", "),
        netDebtService: round2(grossDebtService + debtServiceAdjustment),
      };
    }

    return r;
  });
}

function applyDebtServiceAdjustments(cashflows = [], adjustments = []) {
  if (!cashflows?.length) return [];

  const byDate = new Map();

  for (const adj of adjustments || []) {
    const date = adj?.date ? dayjs(adj.date).format("YYYY-MM-DD") : "";
    const amount = round2(num(adj?.amount, 0));

    if (!date || amount === 0) continue;

    byDate.set(date, [
      ...(byDate.get(date) || []),
      {
        label: adj?.label || "Adjustment",
        amount,
      },
    ]);
  }

  return normalizeBaseDebtServiceFields(cashflows).map((row) => {
    const rowDate = row?.date ? dayjs(row.date).format("YYYY-MM-DD") : "";
    const rowAdjustments = byDate.get(rowDate) || [];

    if (!rowAdjustments.length) return row;

    const existingAdjustment = round2(
      num(row?.debtServiceAdjustment ?? row?.adjustment, 0)
    );
    const addedAdjustment = round2(
      rowAdjustments.reduce((s, a) => s + num(a?.amount, 0), 0)
    );
    const debtServiceAdjustment = round2(existingAdjustment + addedAdjustment);
    const grossDebtService = round2(num(row?.total, 0));

    return {
      ...row,
      adjustments: [
        ...(Array.isArray(row?.adjustments) ? row.adjustments : []),
        ...rowAdjustments,
      ],
      debtServiceAdjustment,
      adjustment: debtServiceAdjustment,
      adjustmentLabel: [
        row?.adjustmentLabel,
        ...rowAdjustments.map((a) => a.label).filter(Boolean),
      ]
        .filter(Boolean)
        .join(", "),
      netDebtService: round2(grossDebtService + debtServiceAdjustment),
    };
  });
}

function applyExistingCashFlows(cashflows = [], baseDebtServiceByPeriod = []) {
  const byDate = new Map();

  for (const base of baseDebtServiceByPeriod || []) {
    const date = base?.date ? dayjs(base.date).format("YYYY-MM-DD") : "";
    const amount = round2(num(base?.total, 0));

    if (!date || amount === 0) continue;

    byDate.set(date, round2(num(byDate.get(date), 0) + amount));
  }

  return normalizeBaseDebtServiceFields(cashflows).map((row) => {
    const date = row?.date ? dayjs(row.date).format("YYYY-MM-DD") : "";
    const existingCashFlows = round2(num(byDate.get(date), 0));
    const baseDebtService = Number.isFinite(Number(row?.netDebtService))
      ? Number(row.netDebtService)
      : num(row?.total, 0);

    return {
      ...row,
      existingCashFlows,
      aggregateCashFlows: round2(baseDebtService + existingCashFlows),
    };
  });
}

/**
 * Build full debt service schedule.
 *
 * Supports separate:
 *   - interest payment dates, based on couponFrequency / firstCouponDate
 *   - principal or sinking fund dates, based on maturity rows
 *
 * This is necessary for issues where interest pays monthly but principal
 * or sinking fund payments occur on a different calendar date.
 */
export function buildDebtServiceSchedule({
  rows = [],
  form,
  debtServiceAdjustments = [],
  baseDebtServiceByPeriod = [],
}) {
  if (!form || !rows?.length) return [];

  const {
    datedDate,
    deliveryDate,
    firstCouponDate,
    finalMaturityDate,
    couponFrequency,
    dayCount,
  } = form;

  const start = dayjs(datedDate);
  const final = dayjs(finalMaturityDate);

  if (!start.isValid() || !final.isValid()) return [];

  const maturities = normalizeTermCoupons(rows)
    .filter((r) => dayjs(r.date).isValid())
    .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());

  const periodsPerYear = getPeriodsPerYear(couponFrequency);
  const stepMonths = Math.round(12 / periodsPerYear);
  const dc = dayCount || "30/360";
  const isLoan = isLoanFinancing(form);

  const interestDates = buildInterestDates({
    datedDate,
    firstCouponDate,
    finalMaturityDate,
    stepMonths,
  });

  const principalDates = buildPrincipalDates({
    maturities,
    datedDate,
    finalMaturityDate,
  });

  const eventDates = Array.from(
    new Set([...interestDates, ...principalDates])
  ).sort(sortIsoDates);

  let periodStart = start;
  let outstandingMaturities = maturities.filter((m) =>
    dayjs(m.date).isAfter(start, "day")
  );

  const cashflows = [];

  for (const eventDateIso of eventDates) {
    const periodEnd = dayjs(eventDateIso);
    if (!periodEnd.isValid() || !periodEnd.isAfter(periodStart, "day")) continue;

    const frac = dayCountFraction(periodStart, periodEnd, dc);

    const loanRate = isLoan
      ? getLoanPeriodRate(outstandingMaturities, periodEnd)
      : null;
    const beginningBalance = isLoan
      ? round2(
          outstandingMaturities.reduce(
            (sum, maturity) => sum + num(maturity?.principal, 0),
            0
          )
        )
      : null;

    const interestRaw = isLoan
      ? beginningBalance * parseCouponAnnual(loanRate) * frac
      : outstandingMaturities.reduce((sum, m) => {
          const p = num(m.principal, 0);
          const c = parseCouponAnnual(m.coupon);
          return sum + p * c * frac;
        }, 0);

    const matured = outstandingMaturities.filter((m) =>
      dayjs(m.date).isSame(periodEnd, "day")
    );

    const principalRaw = matured.reduce((s, m) => s + num(m.principal, 0), 0);

    const interest = round2(interestRaw);
    const principal = round2(principalRaw);
    const total = round2(interest + principal);

    if (interest !== 0 || principal !== 0) {
      cashflows.push({
        date: periodEnd.format("YYYY-MM-DD"),
        beginningBalance,
        rate: isLoan && interest !== 0 ? loanRate : null,
        principal,
        interest,
        total,
      });
    }

    if (principalRaw > 0) {
      outstandingMaturities = outstandingMaturities.filter(
        (m) => !dayjs(m.date).isSame(periodEnd, "day")
      );
    }

    periodStart = periodEnd;

    if (outstandingMaturities.length === 0) break;
  }

  const accruedInterest = computeSettlementAccruedInterest({
    maturities,
    datedDate,
    deliveryDate,
    dayCount: dc,
  });

  let adjustedCashflows = applyFirstDebtServiceAdjustment(
    cashflows,
    accruedInterest
  );

  adjustedCashflows = applyDebtServiceAdjustments(
    adjustedCashflows,
    debtServiceAdjustments
  );

  adjustedCashflows = applyExistingCashFlows(
    adjustedCashflows,
    baseDebtServiceByPeriod
  );

  return adjustedCashflows;
}
